// @flow
import { useForm } from 'hooks/useForm'
import FormsPageContent from 'pages/FormsPage/FormsPageContent'
import { FormsProvider } from 'providers/forms'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { type QueueItem } from 'types/queue-item'
import { ContextTabletMode } from 'utils/ContextTabletMode'
import { ZentakeContext } from 'utils/ZentakeContext'

type Props = {
  tabletMode?: boolean,
}

function FormsPage({ tabletMode = false }: Props): React.Node {
  const history = useHistory()

  const { userDetails, isFetchingForms } = React.useContext(ZentakeContext)

  const {
    loading: loadingForms,
    resources,
    makeSearch,
    search,
    total,
  } = useForm()

  const tabletModeContext = React.useContext(ContextTabletMode)

  const [loading, setLoading] = React.useState(false)
  const [selectedResources, setSelectedResources] = React.useState<
    Array<QueueItem>,
  >([])
  const [hasUrlModal, setHasUrlModal] = React.useState(false)
  const [publicUrl, setPublicUrl] = React.useState(null)
  const [publicFormName, setPublicFormName] = React.useState(null)

  const onPublicFormClick = React.useCallback(
    (id, name, type) => {
      if (!userDetails?.is_verified) {
        toast.warning('Verify Email')
        return
      }

      setHasUrlModal(true)
      setPublicUrl(`${window.location.origin}/f/${id}`)
      setPublicFormName(name)
    },
    [userDetails?.is_verified]
  )

  const onPublicFormDismiss = React.useCallback(() => {
    setHasUrlModal(false)
    setPublicUrl(null)
    setPublicFormName(null)
  }, [])

  const onSelectResource = React.useCallback(
    (resourceId: string) => {
      if (selectedResources?.find((item) => item.id === resourceId)) {
        setSelectedResources(
          selectedResources.filter((item) => item.id !== resourceId)
        )
      } else {
        const resource = resources?.find(
          (resource) => resource.id === resourceId
        )
        if (!resource) return // sanity check
        setSelectedResources([
          ...selectedResources,
          {
            id: resourceId,
            type: resource.type === 'pdf' ? 'document' : 'form',
            has_office_use_questions: resource.has_office_use_questions,
          },
        ])
      }
    },
    [resources, selectedResources]
  )

  const navigateToStartForms = React.useCallback(() => {
    if (!tabletModeContext) return

    const newSessionId = tabletModeContext.createFillSession({
      queue: selectedResources,
    })

    history.push(`/t/f/${newSessionId}`)
  }, [tabletModeContext, selectedResources, history])

  const primaryAction = React.useCallback(() => {
    history.push('/forms/create')
  }, [history])

  return (
    <FormsPageContent
      itemsAsLinks
      formSearch={search}
      hasUrlModal={hasUrlModal}
      isEmpty={resources.length === 0}
      isFetchingForms={isFetchingForms}
      items={resources}
      itemsCount={total}
      loading={loading || loadingForms}
      navigateToStartForms={navigateToStartForms}
      onPublicFormClick={onPublicFormClick}
      onPublicFormDismiss={onPublicFormDismiss}
      onSelectResource={onSelectResource}
      primaryAction={primaryAction}
      primaryActionDataCy="create-form"
      primaryActionText="Create Form"
      publicFormName={publicFormName}
      publicUrl={publicUrl}
      selectedResources={selectedResources}
      setFormSearch={makeSearch}
      setLoading={setLoading}
      tabletMode={tabletMode}
      title="Forms"
    />
  )
}

export default function FormsPageWrapper({ tabletMode, ...props }) {
  return (
    <FormsProvider tabletMode={tabletMode}>
      <FormsPage {...props} tabletMode={tabletMode} />
    </FormsProvider>
  )
}
